import * as Sentry from '@sentry/react';
import { env } from 'env';

import { SentryBreadcrumbCategory, SentryXMLHttpRequest } from '../interfaces';
import {
  BreadcrumbHandlersType,
  CheckCategoryType,
  ISetSentryAuthUserDataProps,
  ISetSentryEventDataProps,
  SanitizedBreadcrumbType,
  SendCustomSentryBreadcrumbType,
} from './interfaces';
import { checkIsLocalhost } from './path';

const { Xhr, Console, UserClick } = SentryBreadcrumbCategory;

export const getSentryEnvironment = (): string => {
  const isLocalhost = checkIsLocalhost();
  return isLocalhost ? 'local' : env.REACT_APP_SENTRY_ENV || 'local';
};

export const sanitizedBreadcrumb: SanitizedBreadcrumbType = (
  breadcrumb,
  hint,
) => {
  const breadcrumbHandlers: BreadcrumbHandlersType = {
    [Xhr]: sanitizedXhrBreadcrumb,
    [Console]: sanitizedConsoleBreadcrumb,
  };

  const handler =
    breadcrumbHandlers[breadcrumb.category as SentryBreadcrumbCategory];

  return handler ? handler(breadcrumb, hint) : breadcrumb;
};

const sanitizedXhrBreadcrumb: SanitizedBreadcrumbType = (breadcrumb, hint) => {
  const xhr = hint.xhr as SentryXMLHttpRequest;
  const data = {
    url: xhr.responseURL,
    body: xhr.__sentry_xhr__ ? xhr.__sentry_xhr__.body : null,
    response: xhr.response,
  };

  return { ...breadcrumb, data };
};

const sanitizedConsoleBreadcrumb: SanitizedBreadcrumbType = (breadcrumb) => {
  delete breadcrumb.data;

  return breadcrumb;
};

export const sendCustomSentryBreadcrumb: SendCustomSentryBreadcrumbType = ({
  message,
  level = 'info',
}) => {
  Sentry.addBreadcrumb({
    category: UserClick,
    message,
    level,
  });
};

export const checkIsTransactionCategory: CheckCategoryType = (category) =>
  category === SentryBreadcrumbCategory.Transaction;

export const checkIsFetchCategory: CheckCategoryType = (category) =>
  category === SentryBreadcrumbCategory.Fetch;

/**
 * Whether to forward full PII (name, email, and geo via the request IP) to
 * Sentry for this build. ON for non-prod envs (dev / qa / uat / sandbox /
 * personal) to make debugging easy; OFF for prod, where the user is identified
 * by id only.
 */
export const shouldSendFullSentryPii = (): boolean =>
  !getSentryEnvironment().includes('prod');

export const setSentryAuthUserData = (user: ISetSentryAuthUserDataProps) => {
  if (shouldSendFullSentryPii()) {
    // Non-prod — forward everything useful for debugging, and let Sentry infer
    // geo from the request IP ({{auto}}).
    Sentry.setUser({ ...user, ip_address: '{{auto}}' });
    return;
  }

  // Prod — identify by id only; no email / name / geo.
  Sentry.setUser({ id: user.id });
};

export const setSentryNotAuthUserData = (email: string) => {
  if (shouldSendFullSentryPii()) {
    Sentry.setUser({ email, ip_address: '{{auto}}' });
    return;
  }

  // Prod — drop the pre-auth email too.
  Sentry.setUser({});
};

export const setSentryEventData = ({ id, name }: ISetSentryEventDataProps) => {
  Sentry.setTag('eventId', id);
  Sentry.setTag('eventName', name);
};

export const setSentrySelectedSpeaker = (activeSpeakerDeviceId: string) => {
  Sentry.setTag('activeSpeakerDeviceId', activeSpeakerDeviceId);
};

export const setSentrySelectedCamera = (activeSpeakerCameraId: string) => {
  Sentry.setTag('activeSpeakerCameraId', activeSpeakerCameraId);
};

export const setSentrySelectedMicrophone = (
  activeMicrophoneDeviceId: string,
) => {
  Sentry.setTag('activeMicrophoneDeviceId', activeMicrophoneDeviceId);
};

export const setSentryFrontendVersion = (version: string) => {
  Sentry.setTag('frontendVersion', version);
};

export const setSentryApiVersion = (version: string) => {
  Sentry.setTag('apiVersion', version);
};

export const reportUEGameliftCrash = (context?: {
  sessionArn?: string;
  region?: string;
  disconnectReason?: string;
  userId?: string;
  streamId?: string;
}) => {
  Sentry.withScope((scope) => {
    // Set crash-specific tags
    scope.setTag('crashType', 'UE_Gamelift_Disconnect');

    if (context?.sessionArn) {
      scope.setTag('gameliftSessionArn', context.sessionArn);
    }

    if (context?.region) {
      scope.setTag('gameliftRegion', context.region);
    }

    if (context?.disconnectReason) {
      scope.setTag('disconnectReason', context.disconnectReason);
    }

    if (context?.userId) {
      scope.setTag('gameliftUserId', context.userId);
    }

    if (context?.streamId) {
      scope.setTag('gameliftStreamId', context.streamId);
    }

    scope.addBreadcrumb({
      category: 'ue.crash',
      message: `UE Crash: Gamelift disconnect - ${
        context?.disconnectReason || 'terminated'
      }`,
      level: 'error',
    });

    // Report the crash as an error with isolated scope
    Sentry.captureException(
      new Error(
        `UE Crash: Gamelift server disconnect - ${
          context?.disconnectReason || 'terminated'
        }`,
      ),
    );
  });
};

export const setSentryError = (error: any) => Sentry.captureException(error);

export const setSentryMessage = (message: string, captureContext?: any) =>
  Sentry.captureMessage(message, captureContext);
